import React, { useState, useEffect, useRef } from 'react'

interface DropdownOption {
  value: string
  label: string
  labelClassName?: string
  selectedClassName?: string
}

interface DropdownProps {
  options: DropdownOption[]
  onSelect: (value: string) => void
  value?: string
  defaultLabel?: string
  defaultValue?: string
  loading?: boolean
  disabled?: boolean
  emptyMessage?: string
  dropdownClassName?: string
  optionClassName?: string
}

const DROPDOWN_SHADOW = `
  4px 4px 20px 4px #2125291A,
  0px 2px 4px 0px #3EB8FD0A,
  2px 4px 10px 4px #3EB8FD0A
`

const CustomizedDropdown: React.FC<DropdownProps> = ({
  options,
  onSelect,
  value,
  defaultLabel,
  defaultValue,
  loading = false,
  disabled = false,
  emptyMessage,
  dropdownClassName = '',
  optionClassName = '',
}) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (defaultValue) {
      const match = options.find((o) => o.value === defaultValue)
      setSelectedOption(match ?? null)
    } else if (defaultLabel) {
      const match = options.find((o) => o.label === defaultLabel)
      setSelectedOption(match ?? null)
    }
  }, [options, defaultLabel, defaultValue])

  useEffect(() => {
    if (value !== undefined) {
      const match = options.find((o) => o.value === value)
      setSelectedOption(match ?? null)
    }
  }, [value, options])

  const handleSelectOption = (option: DropdownOption) => {
    setSelectedOption(option)
    onSelect(option.value)
    setIsOpen(false)
  }

  const toggleDropdown = () => {
    if (!loading && !disabled) {
      setIsOpen((prev) => !prev)
    }
  }

  const activeOption = value ? options.find((o) => o.value === value) : selectedOption
  const hasSelection = Boolean(activeOption)

  const displayLabel = loading
    ? '...loading'
    : (activeOption?.label ?? defaultLabel ?? 'Select an option')

  const triggerLabelClass = hasSelection ? 'text-[#212529]' : 'text-[#6C757D]'
  const triggerChevronClass = hasSelection ? 'text-[#212529]' : 'text-[#6C757D]'

  return (
    <div className="relative text-left" ref={dropdownRef}>
      <button
        type="button"
        className="flex justify-between items-center w-full border border-[#CED4DA] bg-white px-3 sm:px-4 rounded-md py-2 text-xs sm:text-sm md:text-base font-medium focus:outline-none hover:bg-gray-50 transition-colors disabled:opacity-50"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={loading || disabled}
      >
        <span className={`truncate flex-1 text-left font-medium ${triggerLabelClass}`}>
          {displayLabel}
        </span>

        {loading ? (
          <svg
            className="animate-spin h-4 w-4 text-gray-500 ml-2 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : (
          <svg
            className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''} ${triggerChevronClass}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 111.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute mt-2 w-full rounded-[10px] bg-white z-50 ${dropdownClassName}`}
          style={{ boxShadow: DROPDOWN_SHADOW }}
        >
          <ul
            role="listbox"
            className="space-y-2.5 border rounded-[10px] border-[#DEE2E6] py-3 px-2.5"
          >
            {loading ? (
              <li className="px-4 py-2 text-sm text-gray-500 flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Loading options...
              </li>
            ) : options.length > 0 ? (
              options.map((option, index) => {
                const isSelected = option.value === (value ?? selectedOption?.value)
                return (
                  <React.Fragment key={option.value}>
                    <li role="option" aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={() => handleSelectOption(option)}
                        className={[
                          'block w-full text-left px-4 py-2 text-sm font-medium rounded-md',
                          isSelected
                            ? (option.selectedClassName ?? 'bg-gray-100')
                            : 'hover:bg-gray-50',
                          option.labelClassName ?? 'text-gray-700',
                          optionClassName,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {option.label}
                      </button>
                    </li>

                    {index < options.length - 1 && (
                      <li role="separator" aria-hidden="true">
                        <hr className="border-t border-[#E9ECEF] mx-1.5" />
                      </li>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500">
                {emptyMessage ?? 'No options available'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

export default CustomizedDropdown
